import BookshelfIcon from "@assets/compass-sm.png";
import AnnotationsMenu from "@assets/highlight-unselected.png";
import NavMenuIconOpen from "@assets/nav-menu-icon-open.svg";
import NavMenuIcon from "@assets/nav-menu-icon.svg";
import ReadingSettingsIcon from "@assets/reading-settings.svg";
import Search from "@assets/search.svg";
import styled from "@emotion/styled";
import { css, useMediaQuery, useTheme } from "@mui/material";
import Slide from "@mui/material/Slide";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { Link, Outlet } from "react-router";
import { pxToRem } from "../../design/utils";
import { BookmarkButton } from "../bits/bookmarks/BookmarkButton";
import { IconButton } from "../bits/buttons";
import { FlexRowContainer, SeparatorLine } from "../bits/layout";
import { responsiveMargins } from "../bits/layout/cssHelpers";
import { GridItem, GridWrapper } from "../bits/layout/Grid";
import { Header } from "../bits/layout/Header";
import { AnnotationContext } from "../contexts/WithAnnotations/AnnotationContext";
import { PublicationContext } from "../contexts/WithPublication/PublicationContext";
import { SettingsContext } from "../contexts/WithSettings/SettingsContext";
import {
  SidebarContext,
  SidebarState,
} from "../contexts/WithSidebars/SidebarContext";
import { useQuery } from "../hooks/useQuery";
import { EReaderSearchPanel } from "../patterns/EReaderSearchPanel";
import { EReaderViewPanel } from "../patterns/EReaderViewPanel";
import { SettingsMenu } from "../patterns/SettingsMenu";

const EReaderViewWrapper = styled(GridWrapper)`
  font-family: "Literata", sans-serif;
  ${({ theme }) => theme.layers.relief.accent_20.noShadow};
`;

const EReaderHeaderLabel = styled(GridItem)`
  ${responsiveMargins}
  position: relative;
  ${({ theme }) => theme.customTypography.label._1};
  color: ${({ theme }) => theme.colours.accent.dark._30};
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    padding-inline: 0 !important;
    padding-left: 0 !important;
  }
`;

const EReaderHeaderText = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EReaderHeaderTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${pxToRem(6)};
  max-width: calc(100% + ${pxToRem(30)});
  min-width: 0;
  position: relative;
`;

export const EReaderView = () => {
  const { currentSection, setScrollDisabled, publication } =
    useContext(PublicationContext);
  const { settingsOpen, setSettingsOpen } = useContext(SettingsContext);
  const theme = useTheme();
  const isNarrow = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.sm}px)`,
  );
  const isMedium = useMediaQuery(
    `(max-width: ${theme.breakpoints.values.md}px)`,
  );

  const query = useQuery();
  const { setAnnotationsOpen, annotationsOpen } = useContext(AnnotationContext);
  const searchQuery = query.get("search");
  const { sidebarState, setSidebarState, toggleSidebarState } =
    useContext(SidebarContext);
  const toggleMenu = useCallback(
    (sidebarItem: SidebarState) => {
      setScrollDisabled(true);
      toggleSidebarState(sidebarItem);
      if (currentSection) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            const el = document.getElementById(currentSection.id);
            el?.scrollIntoView({ behavior: "instant", block: "start" });
            setScrollDisabled(false);
          });
        }, 300);
      }
    },
    [currentSection, setScrollDisabled, toggleSidebarState],
  );

  useEffect(() => {
    if (searchQuery) {
      setSidebarState(SidebarState.SEARCH);
    } else {
      setSidebarState(SidebarState.NAV);
    }
  }, [searchQuery, setSidebarState]);

  const { open, navOpen, searchOpen } = useMemo(() => {
    const open = sidebarState !== SidebarState.CLOSED;
    return {
      open,
      navOpen: sidebarState === SidebarState.NAV,
      searchOpen: sidebarState === SidebarState.SEARCH,
    };
  }, [sidebarState]);

  return (
    <EReaderViewWrapper columns={12}>
      <Header ereader={true}>
        <GridItem span={4}>
          <FlexRowContainer
            style={{
              justifyContent: isMedium ? "flex-start" : "space-between",
              paddingRight: pxToRem(6),
            }}
          >
            <FlexRowContainer>
              <Link to="/">
                <IconButton
                  css={css`
                    width: 2rem;
                    padding: 0 0.25rem;
                  `}
                  src={BookshelfIcon}
                  alt="Compass"
                  title="Home"
                />
              </Link>
              <SeparatorLine />
              <IconButton
                onClick={() => toggleMenu(SidebarState.NAV)}
                active={navOpen}
                src={navOpen ? NavMenuIcon : NavMenuIconOpen}
                alt="Toggle menu"
                title="Toggle menu"
              />
              {!isMedium ? (
                <p
                  style={{
                    ...theme.customTypography.label._3,
                    maxHeight: pxToRem(33),
                    lineHeight: 1,
                    minWidth: pxToRem(160),
                    userSelect: "none",
                  }}
                >
                  {publication && publication.title}
                </p>
              ) : (
                <SeparatorLine />
              )}
            </FlexRowContainer>
            <FlexRowContainer>
              <IconButton
                onClick={() => toggleMenu(SidebarState.SEARCH)}
                active={searchOpen}
                src={Search}
                title="Search"
                alt="Search"
              />
              <SeparatorLine />
              {isNarrow && (
                <div
                  css={css`
                    align-items: center;
                    display: flex;
                    margin-right: ${pxToRem(8)};
                    min-width: ${pxToRem(34)};
                  `}
                >
                  <BookmarkButton />
                </div>
              )}
            </FlexRowContainer>
          </FlexRowContainer>
        </GridItem>
        <EReaderHeaderLabel
          span={isMedium ? 6 : 7}
          style={{ userSelect: "none" }}
        >
          <EReaderHeaderTitleRow>
            {!isNarrow && (
              <div
                css={css`
                  align-items: center;

                  @media (max-width: ${theme.breakpoints.values.lg}px) {
                    display: flex;
                    justify-content: flex-start;
                    min-width: ${pxToRem(34)};
                  }

                  @media (min-width: ${theme.breakpoints.values.lg + 1}px) {
                    display: flex;
                    height: 100%;
                    left: -42px;
                    position: absolute;
                  }
                `}
              >
                <BookmarkButton />
              </div>
            )}
            <EReaderHeaderText>
              {!isNarrow ? currentSection?.label : ""}
            </EReaderHeaderText>
          </EReaderHeaderTitleRow>
        </EReaderHeaderLabel>
        <GridItem
          span={isMedium ? 2 : 1}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => {
              setScrollDisabled(true);
              setAnnotationsOpen(!annotationsOpen);
              if (currentSection) {
                requestAnimationFrame(() => {
                  const el = document.getElementById(currentSection.id);
                  el?.scrollIntoView({ behavior: "instant", block: "start" });
                  setScrollDisabled(false);
                });
              }
            }}
            active={annotationsOpen}
            src={AnnotationsMenu}
            title="Toggle annotations"
            alt="Annotations menu"
          />
          <SeparatorLine style={{ margin: "0 10px" }} />
          <IconButton
            onClick={() => {
              setSettingsOpen(!settingsOpen);
            }}
            active={settingsOpen}
            src={ReadingSettingsIcon}
            title="Toggle reading settings"
            alt="Settings menu"
          />
        </GridItem>
      </Header>
      <Slide
        direction="right"
        in={open}
        css={css`
          height: calc(100vh - ${pxToRem(44)});
          height: calc(100dvh - ${pxToRem(44)});
          overflow-y: auto;
        `}
      >
        <GridItem span={4} hidden={!open} style={{ userSelect: "none" }}>
          <EReaderViewPanel open={navOpen} />
          <EReaderSearchPanel open={searchOpen} />
        </GridItem>
      </Slide>
      <GridItem
        span={open ? 8 : 12}
        id="ereader-content"
        css={css`
          height: calc(100vh - ${pxToRem(44)});
          height: calc(100dvh - ${pxToRem(44)});
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          @media (max-width: 768px) {
            grid-column: span 12;
          }
        `}
      >
        <Outlet />
      </GridItem>
      <Slide
        direction="left"
        in={settingsOpen}
        css={css`
          height: calc(100vh - ${pxToRem(44)});
          height: calc(100dvh - ${pxToRem(44)});
          overflow-y: auto;
        `}
      >
        <SettingsMenu style={{ display: settingsOpen ? "block" : "none" }} />
      </Slide>
    </EReaderViewWrapper>
  );
};
